import React from "react";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { queryClient } from "App";
import { selectActiveSession } from "app/modules/schools/selectors";
import {
  getAllExpenseHead,
  getAllExpenseSubHead,
  requestExpense,
} from "app/services/ledgerAccount";
import { getTransport } from "app/services/student-management";
import { MenuProps } from "app/utils/helper";
import {
  CustomSelectBox,
  CustomTextField,
} from "assets/styles/globalStyledComponent";

// ============ Styled Components ============
const Label = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  lineHeight: "1.2rem",
  marginBottom: "10px",
  fontWeight: 500,
  color: "#0C2F49",
}));

// ============ Validation Schema ============
const getValidationSchema = () =>
  Yup.object({
    head: Yup.mixed().required("Head Selection is required"),
    subHead: Yup.mixed().required("Sub Head Selection is required"),
    vehicle: Yup.string().when("isVehicle", {
      is: true,
      then: Yup.string().required("Vehicle Selection is required"),
    }),
    amt: Yup.number().required("Amount is required"),
    purpose: Yup.string().required("Purpose is required"),
  });

// ============ Main Component ============
const RequestExpenseForm = ({ dialogs, onSuccess, onOpenSubHeadDialog }) => {
  const selectedSession = useSelector(selectActiveSession);

  // ============ Form ============
  const formik = useFormik({
    initialValues: {
      subHeads: [],
      heads: [],
      head: "",
      subHead: "",
      vehicle: "",
      amt: "",
      purpose: "",
      isVehicle: false,
      transportList: [],
      description: "",
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      const payload = {
        session: selectedSession,
        expenseSubHeadDocId: values.subHead,
        vehicleNumber: values.vehicle,
        requestedAmount: values.amt,
        purpose: values.purpose,
      };
      requestExpenseMutate(payload);
    },
  });

  // ============ API Mutations ============
  const { mutate: requestExpenseMutate } = useMutation(requestExpense, {
    onSuccess: (res) => {
      toast.success(res?.data?.message);
      queryClient.invalidateQueries({ queryKey: "getAllExpense" });
      onSuccess();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
  });

  // ============ API Queries ============
  useQuery({
    queryKey: ["getTransport"],
    queryFn: () => getTransport({ status: "ACTIVE" }),
    onSuccess: (res) => {
      const transportList =
        res?.data?.list?.map((item) => item.vehicleNumber) || [];
      formik.setValues((prev) => ({
        ...prev,
        transportList: transportList,
      }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: formik.values.isVehicle,
  });

  useQuery({
    queryKey: ["getAllExpenseHead", selectedSession],
    queryFn: getAllExpenseHead,
    onSuccess: (res) => {
      const heads =
        res?.data?.list?.map((item) => ({
          id: item._id,
          name: item.expenseHeadName,
        })) || [];

      formik.setValues((prev) => ({
        ...prev,
        ...(dialogs?.headId && { head: dialogs.headId }),
        heads: heads,
      }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: dialogs?.add ? true : false,
  });

  useQuery({
    queryKey: ["getAllExpenseSubHeadForReq", formik.values.head],
    queryFn: () =>
      getAllExpenseSubHead({ expenseHeadDocId: formik.values.head }),
    onSuccess: (res) => {
      formik.setValues((prev) => ({
        ...prev,
        subHeads:
          res?.data?.list?.map((item) => ({
            id: item._id,
            name: item.expenseSubHeadName,
            description: item?.description,
          })) || [],
      }));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message);
    },
    enabled: formik.values.head ? true : false,
  });

  // ============ Render ============
  return (
    <form id="reqExpForm" onSubmit={formik.handleSubmit}>
      <Grid container spacing={1} mt={1}>
        {/* Expense Head */}
        <Grid item xs={6} md={4}>
          <Label>Expense Head</Label>
          <CustomSelectBox
            name="head"
            value={formik.values.head}
            displayEmpty
            fullWidth
            MenuProps={MenuProps}
            onChange={(e) => {
              formik.handleChange(e);
            }}
          >
            <MenuItem value="" disabled>
              Select
            </MenuItem>
            {formik.values?.heads?.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </CustomSelectBox>
          {formik.touched.head && formik.errors.head && (
            <Typography sx={{ color: "red" }}>{formik.errors.head}</Typography>
          )}
        </Grid>

        {/* Sub Head */}
        <Grid item xs={6} md={4}>
          <Label>Sub Head</Label>
          <CustomSelectBox
            name="subHead"
            fullWidth
            disabled={!formik.values.head}
            displayEmpty
            value={formik.values.subHead}
            MenuProps={MenuProps}
          >
            <MenuItem value="" disabled>
              Select
            </MenuItem>
            {formik.values.subHeads?.map((item) => (
              <MenuItem
                key={item.id}
                value={item?.id}
                onClick={() =>
                  formik.setValues((prev) => ({
                    ...prev,
                    subHead: item.id,
                    description: item?.description,
                  }))
                }
              >
                {item.name}
              </MenuItem>
            ))}
            <MenuItem
              sx={{
                color: "blue",
                textDecoration: "underline",
                fontStyle: "italic",
              }}
              value="Add New"
              onClick={() => {
                onOpenSubHeadDialog(formik.values.head);
                formik.setValues((prev) => ({
                  ...prev,
                  subHead: "",
                }));
              }}
            >
              Add New
            </MenuItem>
          </CustomSelectBox>
          {formik.touched.subHead && formik.errors.subHead && (
            <Typography sx={{ color: "red" }}>{formik.errors.subHead}</Typography>
          )}
        </Grid>

        {/* Vehicle Expense */}
        <Grid item xs={6} md={4}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Label>Vehicle Expense ?</Label>
            <FormControlLabel
              sx={{ mt: "-10px" }}
              control={
                <Checkbox
                  checked={formik.values.isVehicle}
                  onChange={(e) =>
                    formik.setValues((prev) => ({
                      ...prev,
                      isVehicle: !prev.isVehicle,
                    }))
                  }
                />
              }
              label="Yes"
            />
          </Stack>
          {formik.values.isVehicle && (
            <>
              <CustomSelectBox
                displayEmpty
                name="vehicle"
                fullWidth
                value={formik.values.vehicle}
                MenuProps={MenuProps}
                onChange={(e) => {
                  formik.handleChange(e);
                }}
              >
                <MenuItem value="" disabled>
                  Select Vehicle
                </MenuItem>
                {formik.values.transportList?.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </CustomSelectBox>
              {formik.touched.vehicle && formik.errors.vehicle && (
                <Typography sx={{ color: "red" }}>
                  {formik.errors.vehicle}
                </Typography>
              )}
            </>
          )}
        </Grid>

        {/* Sub Head Description */}
        <Grid item xs={12}>
          <Label>Sub Head Description</Label>
          <Typography>{formik.values.description || "--"}</Typography>
        </Grid>

        {/* Amount */}
        <Grid item xs={6} md={4}>
          <Label>Amount</Label>
          <CustomTextField
            type="number"
            name="amt"
            fullWidth
            placeholder="Enter Amount"
            value={formik.values.amt}
            onChange={(e) => {
              formik.handleChange(e);
            }}
          />
          {formik.touched.amt && formik.errors.amt && (
            <Typography sx={{ color: "red" }}>{formik.errors.amt}</Typography>
          )}
        </Grid>

        {/* Purpose */}
        <Grid item xs={6} md={8}>
          <Label>Purpose</Label>
          <CustomTextField
            name="purpose"
            fullWidth
            placeholder="Enter Purpose"
            value={formik.values.purpose}
            onChange={(e) => {
              formik.handleChange(e);
            }}
          />
          {formik.touched.purpose && formik.errors.purpose && (
            <Typography sx={{ color: "red" }}>{formik.errors.purpose}</Typography>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

export default RequestExpenseForm;
